import React, { useState } from 'react';
import Section from './Section';
import KpiRow from '../../components/dashboard/KpiRow';
import RFQTable from '../../components/dashboard/RFQTable';
import DashboardWelcome from '../../components/dashboard/welcome/DashboardWelcome';
import type { DashboardRFQ, OwnerView, StageFilter } from '../../hooks/useDashboard';

/**
 * /design gallery — the welcome someone gets when the workspace is busy but
 * nothing in it is theirs, plus the two derived table scopes. This is the case
 * the zero-RFQ first-run page never covered: the org has 47 RFQs, the signed-in
 * person has created none, and before this the page showed KPI tiles reading 47
 * above a table filtered to "My projects" reading 0.
 *
 * Rendered from props only — no backend, no dismissal is ever written from
 * here. The live dashboard derives the same states from the summary's own-RFQ
 * counts and the stored dismissal timestamp.
 */

const noop = () => undefined;

const orgTiles = [
    { label: 'Active RFQs', value: '44', sub: 'across 5 stages' },
    { label: 'Awaiting your review', value: '6', tone: 'action' as const, sub: 'oldest waiting 3 days' },
    { label: 'Suppliers engaged', value: '61', sub: '12 responded this week' },
    {
        label: 'Avg. sourcing cycle', value: '10.8', unit: 'days',
        sub: '↓ 12% vs last quarter', subTone: 'success' as const,
    },
];

const hoursAgo = (h: number) => new Date(Date.now() - h * 3_600_000).toISOString();

const row = (id: string, name: string, stageStep: number, owner: string, partsCount: number, h: number): DashboardRFQ => ({
    id, name, stageStep, owner, partsCount,
    stage: (['Upload BOM', 'Sourcing Criteria', 'Supplier Discovery', 'Supplier Outreach', 'Quotes & Proposals'] as const)[stageStep - 1],
    status: 'DRAFT',
    awarded: false,
    isMine: owner === 'You',
    repliesCount: 0,
    recommendationReady: false,
    pendingOnEndUser: false,
    endUserLabel: null,
    iAmEndUser: false,
    createdAtIso: hoursAgo(h + 240),
    updatedAtIso: hoursAgo(h),
});

const orgRows: DashboardRFQ[] = [
    row('RFQ-142', 'Warehouse AMR Drive Subsystem', 5, 'Marcus T.', 34, 48),
    row('RFQ-139', 'Autonomous Drone Propulsion Kit', 4, 'Alex R.', 18, 6),
    row('RFQ-137', '6-Axis Arm Harmonic Actuators', 3, 'Priya N.', 22, 24),
];

const myRows: DashboardRFQ[] = [row('RFQ-151', 'Gripper Pad Retooling', 2, 'You', 7, 3)];

/** A table fixed to one scope — the point is which scope the page CHOSE. */
const ScopedTable: React.FC<{ scope: OwnerView; rows: DashboardRFQ[] }> = ({ scope, rows }) => {
    const [ownerView, setOwnerView] = useState<OwnerView>(scope);
    const [stageFilter, setStageFilter] = useState<StageFilter>('all');
    const visible = rows.filter((r) => ownerView === 'everyone' || r.isMine);
    return (
        <RFQTable
            rows={visible}
            ownerView={ownerView}
            onOwnerViewChange={setOwnerView}
            stageFilter={stageFilter}
            onStageFilterChange={setStageFilter}
            counts={{ all: visible.length, needsReview: 0 }}
            onRowClick={noop}
        />
    );
};

/** Dismiss here only collapses the local copy; nothing is stored. */
const DismissableWelcome: React.FC = () => {
    const [dismissed, setDismissed] = useState(false);
    return (
        <DashboardWelcome
            firstName="Priya"
            activeTotal={44}
            dismissed={dismissed}
            onStart={noop}
            onDismiss={() => setDismissed(true)}
        />
    );
};

const WelcomeGallery: React.FC = () => (
    <Section title="Dashboard — new person in a workspace that is already running">
        <div className="flex flex-col gap-s6">
            <div className="flex flex-col gap-s3">
                <p className="text-meta text-ink-muted">
                    State 1 — no RFQs of their own, not dismissed: the welcome sits above the
                    workspace's own numbers, which stay readable underneath
                </p>
                <DashboardWelcome
                    firstName="Priya"
                    activeTotal={44}
                    dismissed={false}
                    onStart={noop}
                    onDismiss={noop}
                />
                <span className="text-rail-label uppercase text-ink-muted">Across the workspace</span>
                <KpiRow tiles={orgTiles} />
                <ScopedTable scope="everyone" rows={orgRows} />
            </div>

            <div className="flex flex-col gap-s3">
                <p className="text-meta text-ink-muted">State 2 — dismissed: same action, one line</p>
                <DashboardWelcome
                    firstName="Priya"
                    activeTotal={44}
                    dismissed
                    onStart={noop}
                    onDismiss={noop}
                />
            </div>

            <div className="flex flex-col gap-s3">
                <p className="text-meta text-ink-muted">
                    Live dismiss + re-open ("How I work" re-opens for the session; the stored
                    timestamp is never cleared)
                </p>
                <DismissableWelcome />
            </div>

            <div className="flex flex-col gap-s3">
                <p className="text-meta text-ink-muted">Saving, and a dismissal that never reached the server</p>
                <DashboardWelcome firstName="Priya" activeTotal={44} dismissed={false} onStart={noop} onDismiss={noop} dismissing />
                <DashboardWelcome firstName="Priya" activeTotal={44} dismissed={false} onStart={noop} onDismiss={noop} dismissFailed />
            </div>

            <div className="flex flex-col gap-s3">
                <p className="text-meta text-ink-muted">
                    Derived scope — 0 RFQs of their own defaults to Everyone (never an empty table
                    caused by a filter); ≥1 defaults to My projects. State 3 (has an RFQ) shows no
                    welcome at all, so there is nothing above the tiles.
                </p>
                <ScopedTable scope="everyone" rows={orgRows} />
                <ScopedTable scope="mine" rows={[...orgRows, ...myRows]} />
            </div>
        </div>
    </Section>
);

export default WelcomeGallery;
